import { TrackEventType, Typenames } from '@varo/graphql-schema-types';
import {
  Href,
  NativeStackHeaderProps,
  Stack,
  useFocusEffect,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import ChatInputBox from 'features/chat/ChatInputBox/ChatInputBox';
import ChatStart from 'features/chat/ChatStart/ChatStart';
import useChatAvailability from 'features/chat/hooks/useChatAvailability';
import useChatHistory from 'features/chat/hooks/useChatHistory';
import useTrackEvent from 'features/chat/hooks/useTrackEvent';
import LeaveChat from 'features/chat/LeaveChat/LeaveChat';
import MessageList from 'features/chat/MessageList/MessageList';
import { ChatContext } from 'features/chat/providers/ChatContextProvider';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BackHandler,
  Keyboard,
  Platform,
  StyleSheet,
  View,
} from 'react-native';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import Snackbar from 'shared/components/vxl/Snackbar/Snackbar';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useAutoHideSnackbar } from 'shared/hooks/snackBar/useAutoHideSnackbar';
import theme from 'shared/styles/theme';

const isAndroid = Platform.OS === 'android';

const ChatScreen = () => {
  const router = useRouter();
  const {
    history,
    historyConnectionFetching,
    fetchHistory,
    chatSessionId,
    historyFetchingError,
  } = useChatHistory();
  const { chatAvailability, chatAvailabilityFetching, reExecuteQuery } =
    useChatAvailability();
  const { t: translatedText } = useTranslation('chatStrings');

  const { prevRoute, source } = useLocalSearchParams<{
    prevRoute?: string;
    source?: string;
  }>();

  const { isChatStarted, isChatEndedFromEvent } = useContext(ChatContext);

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  useEffect(() => {
    if (!isAndroid) {
      return;
    }

    const showSub = Keyboard.addListener('keyboardDidShow', (e) => {
      setKeyboardHeight(e.endCoordinates.height);
    });
    const hideSub = Keyboard.addListener('keyboardDidHide', () => {
      setKeyboardHeight(0);
    });

    return () => {
      showSub.remove();
      hideSub.remove();
    };
  }, []);

  useViewDidFocusScreenAnalyticsEvent({
    key: 'supportChatScreen',
    customAttributes: source ? { source_name: source } : null,
  });

  const { trackEvent } = useTrackEvent();

  const isChatAvailable =
    chatAvailability?.__typename === Typenames.ChatAvailabilitySuccess;

  const showLoader =
    (historyConnectionFetching || chatAvailabilityFetching) && !history.length;

  const showChatStart =
    !historyConnectionFetching &&
    !chatAvailabilityFetching &&
    !historyFetchingError &&
    isChatAvailable;

  useEffect(() => {
    if (chatSessionId) {
      trackEvent(TrackEventType.CheckConversationStatus);
    }
  }, [chatSessionId, trackEvent]);

  const handleExitChatAnalytics = usePressableAnalytics({
    analyticsId: 'exitChat',
    customAttributes: {
      chat_session_id: chatSessionId,
      ...(source ? { source_name: source } : {}),
    },
  });

  const goBack = useCallback(() => {
    handleExitChatAnalytics();
    return false;
  }, [handleExitChatAnalytics]);

  useFocusEffect(
    useCallback(() => {
      const backHandler = BackHandler.addEventListener(
        'hardwareBackPress',
        goBack,
      );
      return () => {
        backHandler.remove();
      };
    }, [goBack]),
  );

  useAutoHideSnackbar(Boolean(showSnackbar), () => {
    setShowSnackbar(false);
  });

  return (
    <KeyboardAvoidingView
      style={[
        styles.keyboardContainer,
        isAndroid && {
          marginBottom:
            keyboardHeight > 0
              ? keyboardHeight + theme.spacing.s
              : theme.spacing.s,
        },
      ]}
      behavior="padding"
      enabled={!isAndroid}
      automaticOffset
    >
      <View testID="container" style={styles.container}>
        <Stack.Screen
          options={{
            title: translatedText('title'),
            headerBackVisible: true,
            headerShown: true,
            header: (props: NativeStackHeaderProps) => {
              return (
                <ExpoNavigationBar
                  {...props}
                  primaryIcon={<Icon iconKey="BACK" iconSize="MD" />}
                  primaryOnPress={() => {
                    if (prevRoute) {
                      router.navigate(prevRoute as Href);
                    } else {
                      handleExitChatAnalytics();
                      router.back();
                    }
                  }}
                  {...(chatSessionId
                    ? {
                        trailingAccessories: (
                          <LeaveChat
                            chatSessionId={chatSessionId}
                            isChatEndedFromEvent={isChatEndedFromEvent}
                          />
                        ),
                      }
                    : null)}
                />
              );
            },
          }}
        />
        {showLoader ? (
          <View style={styles.loader}>
            <LoadingSpinner />
          </View>
        ) : (
          <View style={styles.contentWrapper}>
            <MessageList
              messages={history}
              historyConnectionFetching={historyConnectionFetching}
              historyFetchingError={historyFetchingError}
              fetchHistory={fetchHistory}
              chatAvailability={chatAvailability}
              reExecuteAvailability={reExecuteQuery}
            />
            {showChatStart ? (
              <ChatStart setShowSnackbar={setShowSnackbar} />
            ) : null}
            {isChatAvailable && isChatStarted ? <ChatInputBox /> : null}
          </View>
        )}
        {showSnackbar ? (
          <Snackbar text={translatedText('sendMessageError')} />
        ) : null}
      </View>
    </KeyboardAvoidingView>
  );
};
const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    height: '100%',
  },
  contentWrapper: {
    flex: 1,
    height: '100%',
    justifyContent: 'space-between',
    maxHeight: '100%',
    overflow: 'scroll',
  },
  keyboardContainer: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  loader: {
    alignItems: 'center',
  },
});

export default ChatScreen;
